import * as tslib_1 from "tslib";
import MailFolderNode from './MailFolderNode';
import { observer } from 'mobx-react';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import { logMountingPerformance } from 'owa-analytics';
import { keydownHandler } from 'owa-hotkeys';
import { lazyNewFolderFromTextField, lazyRenameFolder, lazyToggleFolderTreeExpansion, } from 'owa-mail-folder-store';
import { hideFolderTreeContextMenu, showFolderTreeContextMenu, } from 'owa-mail-folder-store/lib/actions/folderTreeContextMenu';
import { initiateMailFolderTextField } from 'owa-mail-folder-store/lib/actions/folderTextFieldActions';
import { dismissMailFolderTextField } from 'owa-mail-folder-store/lib/actions/folderTextFieldActions';
import { default as viewStateStore } from 'owa-mail-folder-store/lib/store/store';
import { MailFolderContextMenu } from 'owa-mail-folder-view';
import { FolderTextField } from 'owa-mail-folder-view';
import { getFolderTable, MailboxLoadStateEnum, PRIMARY_FOLDERS_TREE_TYPE, SHARED_FOLDERS_TREE_TYPE, ARCHIVE_FOLDERS_TREE_TYPE, } from 'owa-folders';
import { getCommands } from 'owa-mail-hotkeys';
import { shouldDisplayFolderInFolderTree } from 'owa-folders';
import mailStrings from 'owa-strings/owa-mail-strings';
import TreeNode from 'owa-tree-node/lib/components/TreeNode';
import * as React from 'react';
import { getAnchorForContextMenu } from 'owa-positioning';
import getFolderViewStateFromId from 'owa-mail-folder-store/lib/selectors/getFolderViewStateFromId';
import { LazyPublicFolderPicker } from 'owa-publicfolder-picker';
import { isFeatureEnabled } from 'owa-feature-flags';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { isMailFeatureEnabled } from 'owa-mail-feature-flags';
/**
 * Folder list that renders a root node and all folders supplied to it and their children.
 */
var MailFolderList = /** @class */ (function (_super) {
    tslib_1.__extends(MailFolderList, _super);
    function MailFolderList() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.renderAddNewFolderNode = function () {
            return (React.createElement(TreeNode, { displayName: mailStrings.newFolderAction, isCustomActionNode: true, isRootNode: false, key: 'newFolder', onClick: _this.onNewFolderNodeClicked }));
        };
        _this.onRootNodeContextMenu = function (evt) {
            evt.preventDefault();
            if (_this.shouldShowRootNodeContextMenu()) {
                showFolderTreeContextMenu(_this.props.rootFolder.FolderId.Id, 0 /* Folder */, _this.props.rootFolder, getAnchorForContextMenu(evt), _this.props.treeType);
            }
        };
        _this.onRootNodeChevronClicked = function (evt) {
            evt.stopPropagation();
            _this.props.onRootNodeChevronClickedCallback();
        };
        _this.onNewFolderTextFieldEntry = function (value) {
            lazyNewFolderFromTextField.importAndExecute(value);
        };
        _this.onRenameFolderTextFieldEntry = function (value) {
            lazyRenameFolder.importAndExecute(value);
        };
        _this.onDismissFolderTextFieldEntry = function () {
            dismissMailFolderTextField();
        };
        _this.onContextMenu = function (evt, folder) {
            showFolderTreeContextMenu(folder.FolderId.Id, 0 /* Folder */, folder, getAnchorForContextMenu(evt), _this.props.treeType);
        };
        _this.onContextMenuDismissed = function () {
            hideFolderTreeContextMenu();
        };
        return _this;
    }
    MailFolderList.prototype.render = function () {
        var rootFolder = this.props.rootFolder;
        var contextMenuState = viewStateStore.contextMenuState;
        var shouldShowLoadingSpinner = this.props.loadingState === MailboxLoadStateEnum.Loading;
        var ariaProps = {
            role: AriaRoles.tree,
        };
        if (!rootFolder) {
            return null;
        }
        this.forceExpandFolderTreeIfNecessary();
        return (React.createElement("div", tslib_1.__assign({ style: this.props.style, className: this.props.className }, generateDomPropertiesForAria(ariaProps)),
            this.props.showRootNode && this.renderFolderRoot(),
            this.props.isRootExpanded &&
                shouldShowLoadingSpinner && React.createElement(Spinner, { size: SpinnerSize.medium }),
            // ChildfolderIds would initially be null for Archive root until the root is clicked
            this.props.childFolderIds && this.renderChildren(this.props.childFolderIds, 0),
            this.shouldShowContextMenu() && (React.createElement(MailFolderContextMenu, { anchorPoint: contextMenuState.anchor, folder: contextMenuState.folder, onDismiss: this.onContextMenuDismissed, onDeleteFolder: this.props.onDeleteFolderCallback, shouldHideToggleFavorite: !this.props.isFavoritesSupported })),
            React.createElement(LazyPublicFolderPicker, null)));
    };
    /**
     * Returns whether the context menu should be displayed or not
     */
    MailFolderList.prototype.shouldShowContextMenu = function () {
        var contextMenuState = viewStateStore.contextMenuState;
        // Render the context menu if it is set to be visible and for different folder tree, we render a new context menu
        if (!contextMenuState ||
            !contextMenuState.folder ||
            contextMenuState.treeType != this.props.treeType) {
            return false;
        }
        // Additionally for shared folder trees, we need to check the folderId property because treeType is same for all shared folder roots
        if (contextMenuState.treeType === SHARED_FOLDERS_TREE_TYPE) {
            if (contextMenuState.folder.principalSMTPAddress !=
                this.props.rootFolder.principalSMTPAddress) {
                return false;
            }
        }
        return true;
    };
    MailFolderList.prototype.forceExpandFolderTreeIfNecessary = function () {
        if (!this.props.showRootNode && !this.props.isRootExpanded) {
            // If the 'Folders' root node is hidden, and the root node is collapsed
            // we need to explicitly expand the root node for it to be accessible
            lazyToggleFolderTreeExpansion.importAndExecute();
        }
    };
    MailFolderList.prototype.shouldRenderNewFolderFieldForNode = function (folder) {
        return (viewStateStore.folderTextFieldViewState &&
            viewStateStore.folderTextFieldViewState.folderId == folder.FolderId.Id &&
            viewStateStore.folderTextFieldViewState.operation === 'new');
    };
    MailFolderList.prototype.shouldRenderRenameFolderFieldForNode = function (folder) {
        return (viewStateStore.folderTextFieldViewState &&
            viewStateStore.folderTextFieldViewState.folderId == folder.FolderId.Id &&
            viewStateStore.folderTextFieldViewState.operation === 'rename');
    };
    MailFolderList.prototype.renderFolderTextField = function (nestDepth, onEntry, defaultValue) {
        return (React.createElement(FolderTextField, { depth: nestDepth, defaultValue: defaultValue, key: "folderTextField", onEntry: onEntry, onDismiss: this.onDismissFolderTextFieldEntry }));
    };
    MailFolderList.prototype.renderNode = function (folder, viewState, nestDepth) {
        var _this = this;
        return this.shouldRenderRenameFolderFieldForNode(folder) ? (this.renderFolderTextField(nestDepth, this.onRenameFolderTextFieldEntry, folder.DisplayName)) : (React.createElement(MailFolderNode, { depth: nestDepth, folderId: folder.FolderId.Id, isBeingDragged: viewState.drag.isBeingDragged, key: folder.FolderId.Id, treeType: this.props.treeType, onContextMenu: function (evt) {
                return _this.onContextMenu(evt, folder);
            }, shouldHideToggleFavorite: !this.props.isFavoritesSupported }));
    };
    /**
     *  We should show context menu on root nodes if tree type is shared (for removing shared user option)
     *  and primary tree root for public folders or add shared folder user or folder permissions options
     */
    MailFolderList.prototype.shouldShowRootNodeContextMenu = function () {
        return (this.props.treeType === SHARED_FOLDERS_TREE_TYPE ||
            (this.props.treeType === PRIMARY_FOLDERS_TREE_TYPE &&
                (isFeatureEnabled('pf-picker') ||
                    isMailFeatureEnabled('tri-sharedFolders') ||
                    isMailFeatureEnabled('tri-folderPermissions'))));
    };
    MailFolderList.prototype.renderFolderRoot = function () {
        var chevronProps = {
            isExpanded: this.props.isRootExpanded,
            onClick: this.onRootNodeChevronClicked,
        };
        return (React.createElement(TreeNode, { chevronProps: chevronProps, depth: 0, displayName: this.props.rootDisplayName, isRootNode: true, onClick: chevronProps.onClick, onContextMenu: this.onRootNodeContextMenu }));
    };
    MailFolderList.prototype.renderChildren = function (renderedFolderIds, nestDepth) {
        var _this = this;
        var renderedNodes = [];
        if (nestDepth == 0) {
            var rootFolder = this.props.rootFolder;
            if (this.props.isRootExpanded) {
                // Recursively add subfolders
                var renderedChildNodes = this.renderChildren(renderedFolderIds, nestDepth + 1);
                Array.prototype.push.apply(renderedNodes, renderedChildNodes);
                // Add new folder node or new folder text field at the end
                if (this.shouldRenderNewFolderFieldForNode(rootFolder)) {
                    renderedNodes.push(this.renderFolderTextField(nestDepth, this.onNewFolderTextFieldEntry));
                }
                else if (this.props.treeType === PRIMARY_FOLDERS_TREE_TYPE ||
                    this.props.treeType === ARCHIVE_FOLDERS_TREE_TYPE) {
                    renderedNodes.push(this.renderAddNewFolderNode());
                }
            }
        }
        else {
            renderedFolderIds.forEach(function (folderId) {
                var folder = getFolderTable().get(folderId);
                var viewState = getFolderViewStateFromId(folderId);
                if (shouldDisplayFolderInFolderTree(folder)) {
                    renderedNodes.push(_this.renderNode(folder, viewState, nestDepth));
                    if (_this.shouldRenderNewFolderFieldForNode(folder)) {
                        renderedNodes.push(_this.renderFolderTextField(nestDepth, _this.onNewFolderTextFieldEntry));
                    }
                    if (viewState.isExpanded &&
                        folder.childFolderIds &&
                        folder.childFolderIds.length > 0) {
                        var renderedChildNodes = _this.renderChildren(folder.childFolderIds, nestDepth + 1);
                        Array.prototype.push.apply(renderedNodes, renderedChildNodes);
                    }
                }
            });
        }
        return renderedNodes;
    };
    MailFolderList.prototype.onNewFolderNodeClicked = function (evt) {
        evt.stopPropagation();
        evt.preventDefault();
        var rootFolderId = this.props.rootFolder.FolderId.Id;
        initiateMailFolderTextField(rootFolderId, 'new');
    };
    tslib_1.__decorate([
        autobind,
        keydownHandler(getCommands().newFolder)
    ], MailFolderList.prototype, "onNewFolderNodeClicked", null);
    MailFolderList = tslib_1.__decorate([
        observer,
        logMountingPerformance('MailFolderList')
    ], MailFolderList);
    return MailFolderList;
}(React.Component));
export default MailFolderList;
//# sourceMappingURL=MailFolderList.js.map